import * as Accordion from '@radix-ui/react-accordion';
import type { ReactNode } from 'react';
import { useStore } from '@/state/store';
import { TOOLTIPS } from '@/content/tooltips';
import { fmtCurrency, fmtPct, fmtRatioPct } from '@/lib/format';
import type { DrawFrequency, FilingStatus, PaymentMode, RatePathType, StressPreset, TaxFields } from '@/types';
import { CAP_GAINS_BRACKETS, isBracketTableStale } from '@/engine';
import { CheckboxField, Field, NumberField, SelectField, SliderField } from './inputs/controls';
import { BucketEditor } from './inputs/BucketEditor';
import { TaxProfileControls } from './inputs/TaxProfileControls';

function Section({ value, title, children }: { value: string; title: string; children: ReactNode }) {
  return (
    <Accordion.Item value={value} className="border-b border-slate-200 dark:border-slate-700">
      <Accordion.Header>
        <Accordion.Trigger className="flex w-full items-center justify-between py-2 text-sm font-semibold text-slate-700 hover:text-sky-700 dark:text-slate-200 [&[data-state=open]>span]:rotate-90">
          {title}
          <span className="transition-transform">›</span>
        </Accordion.Trigger>
      </Accordion.Header>
      <Accordion.Content className="pb-4 pt-1">{children}</Accordion.Content>
    </Accordion.Item>
  );
}

const PAYMENT_OPTIONS: { value: PaymentMode; label: string }[] = [
  { value: 'interest_only', label: 'Interest-only — lender minimum' },
  { value: 'fixed', label: 'Fixed payment' },
  { value: 'capitalize', label: 'Pay nothing — interest compounds' },
  { value: 'custom', label: 'Custom schedule' },
];
const FREQ_OPTIONS: { value: DrawFrequency; label: string }[] = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
  { value: 'one_time', label: 'One-time' },
];
const RATEPATH_OPTIONS: { value: RatePathType; label: string }[] = [
  { value: 'flat', label: 'Flat' },
  { value: 'linear', label: 'Linear ramp' },
  { value: 'custom', label: 'Custom anchors' },
  { value: 'stress', label: 'Stress preset' },
];
const STRESS_OPTIONS: { value: StressPreset; label: string }[] = [
  { value: 'rising_300_24m', label: 'Rising +300bps / 24m' },
  { value: 'falling_200_24m', label: 'Falling −200bps / 24m' },
  { value: 'spike_500_revert', label: 'Spike +500 then revert' },
  { value: 'hike_cycle_2022', label: '2022 hike cycle' },
];

function CoreControls() {
  const inputs = useStore((s) => s.inputs);
  const setInputs = useStore((s) => s.setInputs);
  return (
    <Field>
      <SliderField label="Portfolio value" info={TOOLTIPS.portfolioValue} value={inputs.portfolioValue} min={0} max={10_000_000} step={50_000} format={fmtCurrency} onChange={(v) => setInputs({ portfolioValue: v })} />
      <SliderField label="Advance rate" info={TOOLTIPS.blendedAdvanceRate} value={inputs.blendedAdvanceRate} min={0} max={1} step={0.05} format={fmtRatioPct} onChange={(v) => setInputs({ blendedAdvanceRate: v })} />
      <SliderField label="Monthly draw" info={TOOLTIPS.drawAmount} value={inputs.drawAmount} min={0} max={50_000} step={500} format={fmtCurrency} onChange={(v) => setInputs({ drawAmount: v })} />
      <SelectField label="Payment mode" info={TOOLTIPS.paymentMode} value={inputs.paymentMode} options={PAYMENT_OPTIONS} onChange={(v) => setInputs({ paymentMode: v })} />
      <SliderField label="Expected return" info={TOOLTIPS.portfolioExpectedReturnAnnualPct} value={inputs.portfolioExpectedReturnAnnualPct} min={-10} max={20} step={0.5} format={(v) => `${v}%`} onChange={(v) => setInputs({ portfolioExpectedReturnAnnualPct: v })} />
      <SliderField label="Horizon (years)" info={TOOLTIPS.years} value={inputs.years} min={1} max={40} step={1} format={(v) => `${v}y`} onChange={(v) => setInputs({ years: v })} />
    </Field>
  );
}

/**
  * Your rate, as the lender quotes it: a base rate plus their spread (§6.1). SOFR seeds
  * the base rate because that is what the shipped data feed carries, but it is a plain
  * input — a line priced off Prime or a house base rate is modeled by editing it.
  */
function RateComposition() {
  const baseRate = useStore((s) => s.inputs.ratePath.baseRatePct);
  const spreadPct = useStore((s) => s.inputs.spreadPct);
  const ratePath = useStore((s) => s.inputs.ratePath);
  const setInputs = useStore((s) => s.setInputs);
  return (
    <div data-tour="rate" className="rounded-md border border-slate-200 p-2 dark:border-slate-700">
      <Field>
        <NumberField label="Base rate" info={TOOLTIPS.baseRatePct} value={baseRate} step={0.05} onChange={(v) => setInputs({ ratePath: { ...ratePath, baseRatePct: v } })} suffix="%" />
        <NumberField label="Spread" info={TOOLTIPS.spreadPct} value={spreadPct} step={0.05} onChange={(v) => setInputs({ spreadPct: v })} suffix="%" />
      </Field>
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
        Starting APR <strong className="text-slate-700 dark:text-slate-200">{fmtPct(baseRate + spreadPct)}</strong>
      </p>
    </div>
  );
}

const FILING_OPTIONS: { value: FilingStatus; label: string }[] = [
  { value: 'single', label: 'Single' },
  { value: 'mfj', label: 'Married filing jointly' },
  { value: 'mfs', label: 'Married filing separately' },
  { value: 'hoh', label: 'Head of household' },
];

/**
 * Bracketed capital-gains controls (§6.12b). Only the thresholds for the CHOSEN filing
 * status can change the answer, so only those three are exposed — the rest of the table
 * would be noise. Each is prefilled from the stamped IRS table and editable, so a stale
 * default never traps a user waiting on a release.
 */
function BracketControls() {
  const tax = useStore((s) => s.inputs.tax);
  const setInputs = useStore((s) => s.setInputs);
  const inputs = useStore((s) => s.inputs);
  const patchTax = (p: Partial<TaxFields>) => setInputs({ tax: { ...inputs.tax, ...p } });
  const row = CAP_GAINS_BRACKETS.statuses[tax.filingStatus];
  const stale = isBracketTableStale(CAP_GAINS_BRACKETS, new Date().getFullYear());
  const noIncome = tax.otherTaxableIncome === 0 && tax.otherMagi === 0;

  return (
    <div className="flex flex-col gap-2 rounded-md border border-slate-200 p-2 dark:border-slate-700">
      <p className="text-[11px] text-slate-500 dark:text-slate-400">
        {CAP_GAINS_BRACKETS.taxYear} brackets · {CAP_GAINS_BRACKETS.sourceLabel}
        {tax.indexBracketsToInflation
          ? ` · federal thresholds below are year-one figures and grow at ${inputs.inflationAnnualPct}%/yr`
          : ' · thresholds held fixed for the whole projection'}
      </p>
      {stale && (
        <p className="rounded bg-caution-bg px-2 py-1 text-[11px] text-caution" role="status">
          These thresholds are for {CAP_GAINS_BRACKETS.taxYear} and the year has moved on. Edit them
          below, or update the bundled table from the current IRS Revenue Procedure.
        </p>
      )}
      {noIncome && (
        <p className="rounded bg-caution-bg px-2 py-1 text-[11px] text-caution" role="status">
          With no other income entered, your gains stack from zero and most will fall in the 0%
          band — an unrealistically low tax. Enter your income below.
        </p>
      )}
      <SelectField label="Filing status" info={TOOLTIPS.filingStatus} value={tax.filingStatus} options={FILING_OPTIONS} onChange={(v) => patchTax({ filingStatus: v })} />
      <NumberField label="Other taxable income" info={TOOLTIPS.otherTaxableIncome} value={tax.otherTaxableIncome} onChange={(v) => patchTax({ otherTaxableIncome: v })} suffix="$" />
      <NumberField label="Other MAGI" info={TOOLTIPS.otherMagi} value={tax.otherMagi} onChange={(v) => patchTax({ otherMagi: v })} suffix="$" />
      <CheckboxField label="Index brackets to inflation" info={TOOLTIPS.indexBrackets} checked={tax.indexBracketsToInflation} onChange={(c) => patchTax({ indexBracketsToInflation: c })} />
      <NumberField label="0% band up to" info={TOOLTIPS.bracketOverride} value={tax.bracketZeroMaxOverride ?? row.maxZeroRateAmount} onChange={(v) => patchTax({ bracketZeroMaxOverride: v })} suffix="$" />
      <NumberField label="15% band up to" info={TOOLTIPS.bracketOverride} value={tax.bracketFifteenMaxOverride ?? row.maxFifteenRateAmount} onChange={(v) => patchTax({ bracketFifteenMaxOverride: v })} suffix="$" />
      <NumberField label="NIIT threshold" info={TOOLTIPS.niitThreshold} value={tax.niitThresholdOverride ?? row.niitThreshold} onChange={(v) => patchTax({ niitThresholdOverride: v })} suffix="$" />
    </div>
  );
}

/** Full inputs panel — Simple shows the six core controls; Advanced reveals everything (§10.1, §10.2). */
export function InputsPanel() {
  const inputs = useStore((s) => s.inputs);
  const ui = useStore((s) => s.ui);
  const setInputs = useStore((s) => s.setInputs);
  const setUi = useStore((s) => s.setUi);
  const issues = useStore((s) => s.resultIssues);
  const patchTax = (p: Partial<TaxFields>) => setInputs({ tax: { ...inputs.tax, ...p } });

  if (ui.complexityMode === 'simple') {
    return (
      <div className="flex flex-col gap-3">
        <RateComposition />
        <CoreControls />
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Switch to <strong>Advanced</strong> for buckets, taxes, estate, rate paths, and more.
        </p>
        {issues.length > 0 && <Issues issues={issues} />}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {issues.length > 0 && <Issues issues={issues} />}
      <RateComposition />
      <Accordion.Root type="multiple" defaultValue={['portfolio', 'borrowing', 'repayment']}>
        <Section value="portfolio" title="Portfolio & collateral">
          <Field>
            <NumberField label="Portfolio value" info={TOOLTIPS.portfolioValue} value={inputs.portfolioValue} onChange={(v) => setInputs({ portfolioValue: v })} suffix="$" />
            <SliderField label="Blended advance rate" info={TOOLTIPS.blendedAdvanceRate} value={inputs.blendedAdvanceRate} min={0} max={1} step={0.05} format={fmtRatioPct} onChange={(v) => setInputs({ blendedAdvanceRate: v })} />
            <CheckboxField label="Enter loan value directly" info={TOOLTIPS.loanValueOverride} checked={inputs.loanValueOverride !== null} onChange={(c) => setInputs({ loanValueOverride: c ? inputs.portfolioValue * inputs.blendedAdvanceRate : null })} />
            {inputs.loanValueOverride !== null && (
              <NumberField label="Loan value of collateral" info={TOOLTIPS.loanValueOverride} value={inputs.loanValueOverride} onChange={(v) => setInputs({ loanValueOverride: v })} suffix="$" />
            )}
            <NumberField label="Minimum loan value" info={TOOLTIPS.minimumLoanValue} value={inputs.minimumLoanValue} onChange={(v) => setInputs({ minimumLoanValue: v })} suffix="$" />
            <NumberField label="Expected return" info={TOOLTIPS.portfolioExpectedReturnAnnualPct} value={inputs.portfolioExpectedReturnAnnualPct} step={0.5} onChange={(v) => setInputs({ portfolioExpectedReturnAnnualPct: v })} suffix="%" />
            <NumberField label="Dividend yield" info={TOOLTIPS.dividendYieldAnnualPct} value={inputs.dividendYieldAnnualPct} step={0.25} onChange={(v) => setInputs({ dividendYieldAnnualPct: v })} suffix="%" />
            <SelectField label="Dividend handling" info={TOOLTIPS.dividendHandling} value={inputs.dividendHandling} options={[{ value: 'reinvest', label: 'Reinvest' }, { value: 'service_interest', label: 'Service interest' }, { value: 'spend', label: 'Spend' }]} onChange={(v) => setInputs({ dividendHandling: v })} />
            <CheckboxField label="Split into asset buckets" info={TOOLTIPS.useBuckets} checked={inputs.useBuckets} onChange={(c) => setInputs({ useBuckets: c, collateralBuckets: c && inputs.collateralBuckets.length === 0 ? defaultBuckets() : inputs.collateralBuckets })} />
            {inputs.useBuckets && <BucketEditor />}
          </Field>
        </Section>

        <Section value="rate" title="Rate & rate-path builder">
          <Field>
            <SelectField label="Day-count basis" info={TOOLTIPS.dayCountBasis} value={inputs.dayCountBasis} options={[{ value: 360, label: '360' }, { value: 365, label: '365' }]} onChange={(v) => setInputs({ dayCountBasis: v as 360 | 365 })} />
            <SelectField label="Rate path" info={TOOLTIPS.ratePath} value={inputs.ratePath.type} options={RATEPATH_OPTIONS} onChange={(v) => setInputs({ ratePath: { ...inputs.ratePath, type: v } })} />
            {inputs.ratePath.type === 'linear' && (
              <NumberField label="Annual change (bps)" info={TOOLTIPS.annualDeltaBps} value={inputs.ratePath.annualDeltaBps ?? 0} step={25} onChange={(v) => setInputs({ ratePath: { ...inputs.ratePath, annualDeltaBps: v } })} />
            )}
            {inputs.ratePath.type === 'stress' && (
              <SelectField label="Stress preset" info={TOOLTIPS.stressPreset} value={(inputs.ratePath.stressPreset ?? 'rising_300_24m') as StressPreset} options={STRESS_OPTIONS} onChange={(v) => setInputs({ ratePath: { ...inputs.ratePath, stressPreset: v } })} />
            )}
            <NumberField label="Base rate floor" info={TOOLTIPS.baseRateFloorCap} value={inputs.ratePath.floorPct ?? 0} step={0.25} onChange={(v) => setInputs({ ratePath: { ...inputs.ratePath, floorPct: v } })} suffix="%" />
            <NumberField label="Base rate cap" info={TOOLTIPS.baseRateFloorCap} value={inputs.ratePath.capPct ?? 0} step={0.25} onChange={(v) => setInputs({ ratePath: { ...inputs.ratePath, capPct: v === 0 ? null : v } })} suffix="%" />
          </Field>
        </Section>

        <Section value="borrowing" title="Borrowing">
          <Field>
            <NumberField label="Initial balance" info={TOOLTIPS.initialBalance} value={inputs.initialBalance} onChange={(v) => setInputs({ initialBalance: v })} suffix="$" />
            <SelectField label="Draw frequency" info={TOOLTIPS.drawAmount} value={inputs.drawFrequency} options={FREQ_OPTIONS} onChange={(v) => setInputs({ drawFrequency: v })} />
            <NumberField label="Draw amount" info={TOOLTIPS.drawAmount} value={inputs.drawAmount} step={500} onChange={(v) => setInputs({ drawAmount: v })} suffix="$" />
            <NumberField label="Draw start month" info={TOOLTIPS.drawStartMonth} value={inputs.drawStartMonth} onChange={(v) => setInputs({ drawStartMonth: v })} />
            <CheckboxField label="Draw for entire horizon" info={TOOLTIPS.drawDuration} checked={inputs.drawDurationMonths === null} onChange={(c) => setInputs({ drawDurationMonths: c ? null : inputs.years * 12 })} />
            {inputs.drawDurationMonths !== null && (
              <NumberField label="Draw duration (months)" info={TOOLTIPS.drawDuration} value={inputs.drawDurationMonths} onChange={(v) => setInputs({ drawDurationMonths: v })} />
            )}
          </Field>
        </Section>

        <Section value="repayment" title="Repayment">
          <Field>
            <SelectField label="Payment mode" info={TOOLTIPS.paymentMode} value={inputs.paymentMode} options={PAYMENT_OPTIONS} onChange={(v) => setInputs({ paymentMode: v })} />
            {inputs.paymentMode === 'fixed' && (
              <NumberField label="Fixed payment" info={TOOLTIPS.fixedPayment} value={inputs.fixedPayment ?? 0} onChange={(v) => setInputs({ fixedPayment: v })} suffix="$/mo" />
            )}
          </Field>
        </Section>

        <Section value="proceeds" title="Deployed proceeds">
          <Field>
            <CheckboxField label="Invest borrowed proceeds" info={TOOLTIPS.proceedsDeployed} checked={inputs.proceedsDeployed} onChange={(c) => setInputs({ proceedsDeployed: c })} />
            {inputs.proceedsDeployed && (
              <NumberField label="Proceeds return" info={TOOLTIPS.proceedsDeployed} value={inputs.proceedsReturnAnnualPct} step={0.5} onChange={(v) => setInputs({ proceedsReturnAnnualPct: v })} suffix="%" />
            )}
          </Field>
        </Section>

        <Section value="taxes" title="Taxes & estate">
          <Field>
            <TaxProfileControls />
            <NumberField label="Cost basis %" info={TOOLTIPS.costBasis} value={inputs.costBasisPct} step={5} onChange={(v) => setInputs({ costBasisPct: v })} suffix="%" />
            <CheckboxField label="Use tax brackets" info={TOOLTIPS.useBracketedCapGains} checked={inputs.tax.useBracketedCapGains} onChange={(c) => patchTax({ useBracketedCapGains: c })} />
            {!inputs.tax.useBracketedCapGains && (
              <NumberField label="Federal long-term cap-gains (marginal)" info={TOOLTIPS.capGainsRate} value={inputs.tax.longTermCapGainsRatePct} step={1} onChange={(v) => patchTax({ longTermCapGainsRatePct: v })} suffix="%" />
            )}
            {inputs.tax.useBracketedCapGains && <BracketControls />}
            <NumberField label="State cap-gains rate" info={TOOLTIPS.stateCapGainsRate} value={inputs.tax.stateCapGainsRatePct} step={0.5} onChange={(v) => patchTax({ stateCapGainsRatePct: v })} suffix="%" />
            <NumberField label="Ordinary income rate" info={TOOLTIPS.ordinaryIncome} value={inputs.tax.ordinaryIncomeRatePct} step={1} onChange={(v) => patchTax({ ordinaryIncomeRatePct: v })} suffix="%" />
            <CheckboxField label="Interest deductible" info={TOOLTIPS.ordinaryIncome} checked={inputs.tax.interestDeductible} onChange={(c) => patchTax({ interestDeductible: c })} />
            <CheckboxField label="Model estate tax" info={TOOLTIPS.estateTax} checked={inputs.tax.estateTaxEnabled} onChange={(c) => patchTax({ estateTaxEnabled: c })} />
            {inputs.tax.estateTaxEnabled && (
              <>
                <NumberField label="Estate exemption" info={TOOLTIPS.estateTax} value={inputs.tax.estateExemption} onChange={(v) => patchTax({ estateExemption: v })} suffix="$" />
                <NumberField label="Estate tax rate" info={TOOLTIPS.estateTax} value={inputs.tax.estateTaxRatePct} step={1} onChange={(v) => patchTax({ estateTaxRatePct: v })} suffix="%" />
              </>
            )}
          </Field>
        </Section>

        <Section value="projection" title="Projection & display">
          <Field>
            <SliderField label="Horizon (years)" info={TOOLTIPS.years} value={inputs.years} min={1} max={40} step={1} format={(v) => `${v}y`} onChange={(v) => setInputs({ years: v })} />
            <NumberField label="Inflation" info={TOOLTIPS.inflation} value={inputs.inflationAnnualPct} step={0.25} onChange={(v) => setInputs({ inflationAnnualPct: v })} suffix="%" />
            <CheckboxField label="Show in today's dollars" info={TOOLTIPS.inflation} checked={ui.showRealDollars} onChange={(c) => void setUi({ showRealDollars: c })} />
            <CheckboxField label="Model forced liquidation" info={TOOLTIPS.forcedLiquidation} checked={inputs.modelForcedLiquidation} onChange={(c) => setInputs({ modelForcedLiquidation: c })} />
            <NumberField label="Cure target LTV" info={TOOLTIPS.forcedLiquidation} value={inputs.cureTargetLtv} step={0.05} onChange={(v) => setInputs({ cureTargetLtv: v })} />
          </Field>
        </Section>
      </Accordion.Root>
    </div>
  );
}

function Issues({ issues }: { issues: string[] }) {
  return (
    <div className="rounded border border-danger/40 bg-danger-bg px-2 py-1 text-xs text-danger" role="alert">
      {issues.map((i, idx) => (
        <div key={idx}>{i}</div>
      ))}
    </div>
  );
}

function defaultBuckets() {
  return [
    { id: crypto.randomUUID(), label: 'Cash & money-market', assetClass: 'cash_mmf' as const, value: 200_000, advanceRate: 0.95, eligible: true, expectedReturnAnnualPct: 4, volatilityPct: 0, dividendYieldAnnualPct: 0, costBasis: 200_000 },
    { id: crypto.randomUUID(), label: 'Broad ETF / large-cap equity', assetClass: 'broad_etf_large_cap' as const, value: 800_000, advanceRate: 0.7, eligible: true, expectedReturnAnnualPct: 7, volatilityPct: 15, dividendYieldAnnualPct: 1.5, costBasis: 400_000 },
  ];
}
